"use client";

import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const disciplines = [{
  title: "National SEO",
  body: "I build authority for UK-wide brands, ensuring your business ranks for high-intent keywords across the country. My strategy focuses on sustainable growth, technical excellence, and content that resonates with your national audience to drive consistent, high-quality organic traffic.",
  linkLabel: "View National SEO",
  glyph: <svg viewBox="0 0 48 48" className="w-12 h-12" fill="none" aria-hidden="true">
        <path className="marker-path" d="M4 26 C 10 14, 18 14, 22 26 S 34 38, 44 22" stroke="var(--primary)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
}, {
  title: "International SEO",
  body: "Expanding into new markets requires precision. I manage multi-region and multi-language SEO strategies, ensuring your site is correctly indexed and optimised for global search engines. I help you capture international demand while maintaining local relevance.",
  linkLabel: "View International SEO",
  glyph: <svg viewBox="0 0 48 48" className="w-12 h-12" fill="none" aria-hidden="true">
        <path className="marker-path" d="M8 10 L 38 10 L 40 40 L 12 42 L 6 34 Z M 6 34 L 14 32 L 12 42" stroke="var(--primary)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
}, {
  title: "Local SEO",
  body: "I help businesses dominate their local search results. From Google Business Profile optimisation to local citation management, I ensure your brand is the first choice for customers in your area, driving foot traffic and local enquiries effectively.",
  linkLabel: "View Local SEO",
  glyph: <svg viewBox="0 0 48 48" className="w-12 h-12" fill="none" aria-hidden="true">
        <path className="marker-path" d="M14 24 a 8 8 0 1 1 16 0 a 8 8 0 1 1 -16 0 M 22 24 a 8 8 0 1 0 16 0 a 8 8 0 1 0 -16 0" stroke="var(--primary)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
}, {
  title: "AI Search Optimisation",
  body: "Modern search is evolving. I optimise your digital presence for AI-powered search experiences and answer engines. I ensure your brand remains visible and authoritative as search behaviour shifts toward conversational and AI-driven results.",
  linkLabel: "View AI Search",
  glyph: <svg viewBox="0 0 48 48" className="w-12 h-12" fill="none" aria-hidden="true">
        <path className="marker-path" d="M6 42 L 42 6" stroke="var(--primary)" strokeWidth="5" strokeLinecap="round" />
      </svg>
}];
export default function Services() {
  const container = useRef<HTMLElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      const paths = el.querySelectorAll<SVGPathElement>(".marker-path");
      paths.forEach(p => {
        const len = p.getTotalLength();
        p.style.strokeDasharray = `${len}`;
        p.style.strokeDashoffset = `${len}`;
      });
      gsap.set(".discipline-card", {
        y: 24
      });
      gsap.to(".discipline-card", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.12,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        },
        onComplete: () => {
          gsap.to(".marker-path", {
            strokeDashoffset: 0,
            duration: 0.7,
            ease: "power2.out",
            stagger: 0.12
          });
        }
      });
      const refresh = () => ScrollTrigger.refresh();
      requestAnimationFrame(refresh);
      if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
        once: true
      });
      document.fonts?.ready.then(refresh);
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".discipline-card", {
        opacity: 1,
        y: 0
      });
      const paths = el.querySelectorAll<SVGPathElement>(".marker-path");
      paths.forEach(p => {
        p.style.strokeDasharray = "none";
        p.style.strokeDashoffset = "0";
      });
    });
  }, {
    scope: container
  });
  return <section data-section-id="30724" id="industriesservedgrid" ref={container} data-nav="light" className="bg-background border-t border-border py-32 md:py-44">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 mb-20 md:mb-28">
          <div className="md:col-span-3">
            <span className="inline-block font-sans text-[14px] font-medium uppercase leading-none" style={{
            letterSpacing: "0.18em",
            color: "var(--muted-foreground)"
          }}>
              MY SERVICES
            </span>
          </div>
          <div className="md:col-span-9">
            <h1 className="font-serif text-foreground" style={{
            fontSize: "clamp(30px, 2.8vw, 42px)",
            lineHeight: 0.96,
            letterSpacing: "-0.028em",
            fontWeight: 400
          }}>
              Expert SEO <em className="italic">strategies</em> for growth.
            </h1>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {disciplines.map((d, i) => <article key={i} data-index={i} className="discipline-card group relative flex flex-col p-8 md:p-12 transition-transform duration-[250ms] ease-out hover:-translate-y-0.5" style={{
          opacity: 0,
          backgroundColor: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-lg)",
          minHeight: "360px"
        }}>
              <div className="mb-8">{d.glyph}</div>

              <h3 className="font-serif text-foreground mb-5" style={{
            fontSize: "clamp(24px, 1.8vw, 30px)",
            lineHeight: 1.02,
            letterSpacing: "-0.02em",
            fontWeight: 400
          }}>
                {d.title}
              </h3>

              <p className="font-sans mb-8" style={{
            fontSize: "18px",
            lineHeight: 1.6,
            color: "var(--muted-foreground)"
          }}>
                {d.body}
              </p>

              <div className="mt-auto pt-4">
                <Link to="/ServicesPage" className="inline-flex items-center gap-2 font-sans text-foreground group/link" style={{
              fontSize: "16px",
              fontWeight: 500
            }}>
                  <span className="relative">
                    {d.linkLabel}
                    <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-current transition-[width] duration-300 ease-out group-hover/link:w-full" aria-hidden="true" />
                  </span>
                  <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 ease-out group-hover/link:translate-x-1" />
                </Link>
              </div>
            </article>)}
        </div>
      </div>
    </section>;
}